// deps
import React, { memo, ReactElement, useCallback, useMemo } from "react";

// interfaces
import { AddSwiggyMoneyProps, ViolationType } from "@payment-ui/interfaces";

// constants
import { SWIGGY_MONEY_HEADER_TEXTS_V4 } from "@payment-ui/constants/SwiggyMoney";

// components
import Offers from "./components/Offers/Offers";
import SwiggyMoneyNotes from "./components/Notes/Notes";
import { AmountSelectionRadioWidget } from "../widgets/AmountSelectionRadioWidget";
import { RedeemVoucher } from "@payment-ui/components/redeem-voucher";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// helpers
import { getAmountViolationInfo } from "@payment-ui/helpers/SwiggyInstrument";
import { format } from "@swiggy-private/core-ui";

// test-ids
import { ADD_SWIGGY_MONEY_TEST_IDS } from "@swiggy-private/payment-test-ids";
import {
    Container,
    ContentContainer,
    OffersAmountContainer,
    OffersContainer,
    ProceedContainer,
    Separator,
} from "./styles";

function AddSwiggyMoney(props: AddSwiggyMoneyProps): ReactElement {
    const {
        offers,
        eligibilityDetails,
        onAddMoney,
        selectionAmount,
        updateSelectedAmount,
        clickPredefinedAmount,
        swiggyMoneyInfoActions,
        isSwMoneyBusinessLine,
        redirectToVoucher,
    } = props;
    const { validationInfo, suggestedAmounts = [], addMoneyNotes } = eligibilityDetails;
    /* 
    /* Handling following cases to open custom input section
    /* - amount suggestions are empty , should gracefully fallback to custom input
    /* - selection amount is zero, in case no value is supposed to be preselected
    */

    const violationInfo = useMemo(() => getAmountViolationInfo(selectionAmount, validationInfo), [
        selectionAmount,
        validationInfo,
    ]);
    const isInvalidAmount = useMemo(() => violationInfo.type == ViolationType.ERROR, [violationInfo.type]);

    const amountSuggestionOptions = useMemo(
        () =>
            suggestedAmounts.map(suggestion => {
                return { ...suggestion };
            }),
        [suggestedAmounts],
    );

    const smHeaderAriaLabel = useMemo(
        () => format(SWIGGY_MONEY_HEADER_TEXTS_V4.accessbilityAddMoneyText, String(selectionAmount)),
        [selectionAmount],
    );

    const onProceedToPayment = useCallback(() => {
        if (isInvalidAmount) return;
        onAddMoney();
    }, [isInvalidAmount, onAddMoney]);

    return (
        <Container data-testid={ADD_SWIGGY_MONEY_TEST_IDS.CONTAINER}>
            <ContentContainer>
                <OffersAmountContainer>
                    <AmountSelectionRadioWidget
                        reflectChangedAmountImmediately={true}
                        inputFieldPlaceHolder={SWIGGY_MONEY_HEADER_TEXTS_V4.defaultPlaceHolderOnCustomAmount}
                        amountOptions={amountSuggestionOptions}
                        amount={selectionAmount}
                        onUpdateAmount={updateSelectedAmount}
                        inputScrollIntoView={100}
                        swiggyMoneyInfoHalfCard={eligibilityDetails?.learnMoreHalfCard}
                        violationInfo={violationInfo}
                        // FOR ANALYTICS
                        clickPredefinedAmount={clickPredefinedAmount}
                        swiggyMoneyInfoActions={swiggyMoneyInfoActions}
                    />
                    <OffersContainer>
                        <Offers offers={offers} />
                    </OffersContainer>
                </OffersAmountContainer>
                {/* CTA only shown in case of user coming from swiggy money flow */}
                {isSwMoneyBusinessLine && (
                    <>
                        <Separator />
                        <RedeemVoucher
                            labelText={SWIGGY_MONEY_HEADER_TEXTS_V4.haveVoucher}
                            btnText={SWIGGY_MONEY_HEADER_TEXTS_V4.redeemNow}
                            onButtonClick={redirectToVoucher}
                        />
                    </>
                )}
            </ContentContainer>

            <SwiggyMoneyNotes title={SWIGGY_MONEY_HEADER_TEXTS_V4.notesHeader} notes={addMoneyNotes} />

            <ProceedContainer>
                <SuperButton
                    onClick={onProceedToPayment}
                    isDisabled={isInvalidAmount}
                    ariaLabel={smHeaderAriaLabel}
                    data-testid={ADD_SWIGGY_MONEY_TEST_IDS.PROCEED_BTN}
                    buttonType="specialPositive"
                    buttonSize="large"
                >
                    {SWIGGY_MONEY_HEADER_TEXTS_V4.addMoneyCtaText}
                </SuperButton>
            </ProceedContainer>
        </Container>
    );
}

export default memo(AddSwiggyMoney);
